/**
 * ChangesPanel — historia + diff viewer dla wykrytych zmian w SWZ (D3).
 *
 * Pokazuje:
 *  - Listę zmian chronologicznie (newest first)
 *  - Severity badges (critical/important/info)
 *  - Diff preview (200 linii) z możliwością rozwinięcia do pełnego
 *  - Acknowledge button (clears badge)
 *  - "Sprawdź teraz" — wymusza re-download + check
 *
 * Severity colors:
 *  critical  → red (deadline / wadium changed)
 *  important → amber (specs / docs / corrigendum)
 *  info      → blue (Q&A / minor)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  AlertTriangle, Clock, FileText, Bell, BellOff, RefreshCw, Loader2,
  Check, ChevronDown, ChevronRight, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getTenderChanges,
  getChangeFullDiff,
  acknowledgeChange,
  triggerSwzChangeCheck,
} from '../../lib/api';

const SEVERITY_CONFIG = {
  critical: {
    label_pl: 'KRYTYCZNA',
    label_en: 'CRITICAL',
    badge: 'bg-red-500/20 text-red-300 border-red-500/40',
    border: 'border-red-500',
    icon: AlertTriangle,
  },
  important: {
    label_pl: 'WAŻNA',
    label_en: 'IMPORTANT',
    badge: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    border: 'border-amber-500',
    icon: AlertTriangle,
  },
  info: {
    label_pl: 'info',
    label_en: 'info',
    badge: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    border: 'border-blue-500',
    icon: Bell,
  },
  quiet: {
    label_pl: 'cicha',
    label_en: 'quiet',
    badge: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
    border: 'border-zinc-700',
    icon: BellOff,
  },
};

const CATEGORY_LABELS_PL = {
  deadline: 'Termin / Godzina',
  wadium: 'Wadium / Zabezpieczenie',
  specs: 'Opis przedmiotu',
  docs: 'Wymagane dokumenty',
  qa: 'Wyjaśnienia / Q&A',
  corrigendum: 'Sprostowanie',
  other: 'Inne',
};

const CATEGORY_LABELS_EN = {
  deadline: 'Deadline / time',
  wadium: 'Bid bond',
  specs: 'Subject matter',
  docs: 'Required documents',
  qa: 'Q&A',
  corrigendum: 'Corrigendum',
  other: 'Other',
};


function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pl-PL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso.slice(0, 16);
  }
}


function DiffViewer({ diffText, language = 'pl' }) {
  if (!diffText) {
    return (
      <div className="text-xs text-muted-foreground italic p-2">
        {language === 'pl' ? 'Brak treści diff' : 'No diff content'}
      </div>
    );
  }
  return (
    <pre className="text-[11px] font-mono whitespace-pre-wrap leading-relaxed bg-zinc-950 rounded p-3 max-h-96 overflow-auto border border-border">
      {diffText.split('\n').map((line, idx) => {
        let cls = 'text-zinc-400';
        if (line.startsWith('+++') || line.startsWith('---')) cls = 'text-cyan-400 font-semibold';
        else if (line.startsWith('@@')) cls = 'text-purple-400 font-semibold';
        else if (line.startsWith('+')) cls = 'text-emerald-300 bg-emerald-500/10';
        else if (line.startsWith('-')) cls = 'text-red-300 bg-red-500/10';
        return <div key={idx} className={cls}>{line || ' '}</div>;
      })}
    </pre>
  );
}


function ChangeCard({ change, lang, onAcknowledge }) {
  const [expanded, setExpanded] = useState(false);
  const [fullDiff, setFullDiff] = useState(null);
  const [loadingFull, setLoadingFull] = useState(false);
  const [acking, setAcking] = useState(false);

  const cfg = SEVERITY_CONFIG[change.severity] || SEVERITY_CONFIG.info;
  const Icon = cfg.icon;
  const cat = change.primary_category || 'other';
  const catLabel = (lang === 'pl' ? CATEGORY_LABELS_PL : CATEGORY_LABELS_EN)[cat] || cat;

  async function loadFullDiff() {
    if (fullDiff) return;
    setLoadingFull(true);
    try {
      const r = await getChangeFullDiff(change.id);
      setFullDiff(r);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'pl' ? 'Błąd pobierania diff' : 'Diff fetch error'));
    } finally {
      setLoadingFull(false);
    }
  }

  function toggleExpand() {
    const next = !expanded;
    setExpanded(next);
    if (next && !fullDiff) loadFullDiff();
  }

  async function handleAck() {
    setAcking(true);
    try {
      await acknowledgeChange(change.id);
      toast.success(lang === 'pl' ? 'Oznaczono jako przejrzane' : 'Marked as reviewed');
      if (onAcknowledge) onAcknowledge(change.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'pl' ? 'Błąd' : 'Error'));
    } finally {
      setAcking(false);
    }
  }

  return (
    <div className={`border-l-4 ${cfg.border} bg-card rounded-r-lg overflow-hidden`} data-testid={`change-${change.id}`}>
      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge variant="outline" className={`gap-1 ${cfg.badge}`}>
                <Icon className="w-3 h-3" />
                {(lang === 'pl' ? cfg.label_pl : cfg.label_en).toUpperCase()}
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {catLabel}
              </Badge>
              {change.is_acknowledged && (
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 gap-1 text-[10px]">
                  <Check className="w-3 h-3" />
                  {lang === 'pl' ? 'przejrzane' : 'reviewed'}
                </Badge>
              )}
              <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {fmtDateTime(change.detected_at)}
              </span>
            </div>
            <div className="text-sm font-medium">
              {lang === 'pl' ? change.headline_pl : (change.headline_en || change.headline_pl)}
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
              <FileText className="w-3 h-3" />
              <span className="truncate">{change.filename}</span>
              <span>·</span>
              <span>v{change.previous_version_num} → v{change.version_num}</span>
              <span>·</span>
              <span className="text-emerald-400">+{change.diff_stats?.added_lines || 0}</span>
              <span className="text-red-400">-{change.diff_stats?.removed_lines || 0}</span>
              <span className="text-zinc-500">
                · podobieństwo {(change.diff_stats?.similarity_pct || 0).toFixed(1)}%
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleExpand}
              className="h-7 px-2 gap-1"
              data-testid={`change-toggle-${change.id}`}
            >
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {lang === 'pl' ? 'Diff' : 'Diff'}
            </Button>
            {!change.is_acknowledged && (
              <Button
                size="sm"
                variant="default"
                onClick={handleAck}
                disabled={acking}
                className="h-7 px-2 gap-1"
                data-testid={`change-ack-${change.id}`}
              >
                {acking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {lang === 'pl' ? 'Przejrzałem' : 'Reviewed'}
              </Button>
            )}
          </div>
        </div>

        {expanded && (
          <div className="pt-2 border-t border-border space-y-2">
            {loadingFull && (
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                {lang === 'pl' ? 'Ładuję pełen diff…' : 'Loading full diff…'}
              </div>
            )}
            {!loadingFull && (
              <DiffViewer
                diffText={(fullDiff && fullDiff.unified) || change.diff_preview || ''}
                language={lang}
              />
            )}
            {fullDiff && (
              <div className="text-[10px] text-muted-foreground">
                {lang === 'pl' ? 'Pełen diff' : 'Full diff'}: {fullDiff.old_text_length} → {fullDiff.new_text_length} chars
              </div>
            )}
            {(change.categories || []).length > 1 && (
              <div className="text-[10px] text-muted-foreground">
                {lang === 'pl' ? 'Inne kategorie' : 'Other categories'}:{' '}
                {change.categories
                  .filter((c) => c !== change.primary_category)
                  .map((c) => (lang === 'pl' ? CATEGORY_LABELS_PL : CATEGORY_LABELS_EN)[c] || c)
                  .join(', ')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


export default function ChangesPanel({ tenderId, lang = 'pl', onUnacknowledgedCountChange }) {
  const [items, setItems] = useState([]);
  const [unack, setUnack] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [includeQuiet, setIncludeQuiet] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getTenderChanges(tenderId, includeQuiet);
      setItems(r.items || []);
      setUnack(r.unacknowledged_count || 0);
      if (onUnacknowledgedCountChange) onUnacknowledgedCountChange(r.unacknowledged_count || 0);
    } catch (err) {
      // 404 = no changes yet — OK
    } finally {
      setLoading(false);
    }
  }, [tenderId, includeQuiet, onUnacknowledgedCountChange]);

  useEffect(() => { load(); }, [load]);

  async function handleManualCheck() {
    setChecking(true);
    try {
      const r = await triggerSwzChangeCheck(tenderId);
      if (r.status === 'no_source') {
        toast.info(lang === 'pl' ? r.message : 'Source unavailable');
      } else if ((r.changes_count || 0) === 0) {
        toast.success(lang === 'pl'
          ? `Sprawdzono ${r.files_compared} plików — brak zmian`
          : `Checked ${r.files_compared} files — no changes`);
      } else {
        toast.success(lang === 'pl'
          ? `Wykryto ${r.changes_count} zmian!`
          : `${r.changes_count} changes detected!`);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'pl' ? 'Błąd sprawdzania' : 'Check error'));
    } finally {
      setChecking(false);
    }
  }

  function handleAcknowledged(changeId) {
    setItems((prev) => prev.map((it) => it.id === changeId ? { ...it, is_acknowledged: true } : it));
    setUnack((n) => Math.max(0, n - 1));
    if (onUnacknowledgedCountChange) onUnacknowledgedCountChange(Math.max(0, unack - 1));
  }

  return (
    <Card data-testid="changes-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            {lang === 'pl' ? 'Zmiany SWZ (D3)' : 'SWZ Changes (D3)'}
            {unack > 0 && (
              <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">
                {unack} {lang === 'pl' ? 'do przejrzenia' : 'unreviewed'}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIncludeQuiet(!includeQuiet)}
              className="h-7 text-xs"
              title={lang === 'pl' ? 'Pokaż również zmiany whitespace-only' : 'Show whitespace-only changes too'}
            >
              {includeQuiet ? <BellOff className="w-3 h-3 mr-1" /> : <Bell className="w-3 h-3 mr-1" />}
              {includeQuiet
                ? (lang === 'pl' ? 'Tylko ważne' : 'Important only')
                : (lang === 'pl' ? 'Pokaż wszystkie' : 'Show all')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualCheck}
              disabled={checking}
              className="h-7 gap-1"
              data-testid="changes-check-now"
            >
              {checking ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {lang === 'pl' ? 'Sprawdź teraz' : 'Check now'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          {lang === 'pl'
            ? 'Cron co 6h re-pobiera SWZ i porównuje SHA256. Wykryte zmiany klasyfikowane (deadline / wadium / specs / docs / Q&A / sprostowanie) i scorowane (krytyczne / ważne / info).'
            : 'Cron every 6h re-downloads SWZ and compares SHA256. Detected changes are classified (deadline / wadium / specs / docs / Q&A / corrigendum) and severity-scored (critical / important / info).'}
        </p>

        {loading && (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-sm text-muted-foreground py-6 text-center">
            <Bell className="w-6 h-6 mx-auto mb-2 opacity-40" />
            {lang === 'pl'
              ? 'Nie wykryto jeszcze żadnych zmian. Najpierw pobierz SWZ (zakładka Załączniki) — kolejne wersje porównamy automatycznie.'
              : 'No changes detected yet. Download SWZ first (Attachments tab) — subsequent versions are compared automatically.'}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="space-y-2">
            {items.map((ch) => (
              <ChangeCard
                key={ch.id}
                change={ch}
                lang={lang}
                onAcknowledge={handleAcknowledged}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
